<template>
  <div v-if="!loading" class="legal-container">
    <div class="legal-inner-container justify-content-center">
      <div class="spacing-32"></div>
      <div class="spacing-32"></div>
      <div
        class="drip-h2 font-weight-700 color-noble-blue-500 color-noble-blue-500 mb-4 mt-4 mt-md-0"
      >
        Update Your PAN Details
      </div>
      <div class="documents-section">
        <div
          class="drip-body color-noble-blue-500 font-weight-normal text-justify"
        >
          In order to enhance our internal compliance processes and to manage
          your account better, we request you to update your PAN details. This
          helps us verify each authorized user on the portal and ensures
          seamless access to all features.
        </div>
        <div class="spacing-24"></div>
        <alert :showCta="false">
          <template slot="icon">
            <img
              src="@/assets/images/common/error-outline-white.svg"
              alt="Error Icon"
            />
          </template>
          <template slot="message">
            Please update your PAN details to proceed further.
          </template>
        </alert>
        <div class="drip-label color-noble-blue-500 text-left">
          Please verify the pre-populated details and then fill in your PAN
          below:
        </div>
      </div>
      <div class="spacing-12"></div>
      <form class="legal-input-container" @submit.prevent="submitPanCard">
        <div class="d-flex flex-column color-noble-blue-500">
          <div class="drip-body width-200 color-noble-blue-500">Name</div>
          <div class="spacing-4"></div>
          <div
            class="drip-body font-weight-400 color-black text-left text-capitalize"
          >
            {{ $store.getters.user.name }}
          </div>
        </div>
        <div class="spacing-12"></div>
        <div class="d-flex flex-column color-noble-blue-500">
          <div class="drip-body width-200 color-noble-blue-500">Email</div>
          <div class="spacing-4"></div>
          <div class="drip-body font-weight-400 color-black text-left">
            {{ $store.getters.user.email }}
          </div>
        </div>
        <div class="spacing-12"></div>
        <div class="d-flex flex-column color-noble-blue-500">
          <div class="drip-body width-200 color-noble-blue-500">
            Phone Number
          </div>
          <div class="spacing-4"></div>
          <div class="drip-body font-weight-400 color-black text-left">
            {{ $store.getters.user.phone }}
          </div>
        </div>
        <div class="spacing-12"></div>
        <div class="input-container">
          <div class="input-label drip-body font-weight-bold">PAN</div>
          <div class="input-block">
            <input
              id="pancard"
              name="pancard"
              data-vv-as="PAN"
              type="text"
              class="w-100"
              v-model="pancard"
              @input="pancard = pancard.toUpperCase()"
              placeholder="Enter your PAN"
              v-validate="'required|length_format|pan_format'"
            />
            <span v-if="errors.first('pancard')" class="error">
              {{ errors.first('pancard') }}
            </span>
          </div>
        </div>
        <action-button variant="solid" type="submit" extraClass="fullWidth">
          <b-icon-arrow-repeat
            v-if="loader"
            class="mr-2"
            animation="spin"
            scale="0.8"
          />
          Update PAN Details
          <img
            v-if="!loader"
            src="@/assets/images/common/chevron-right-icon.svg"
            height="14"
            class="ml-2"
          />
        </action-button>
      </form>

      <div class="spacing-32"></div>
      <div class="spacing-16"></div>
    </div>
  </div>
  <div v-else>
    <loader :height="300" />
  </div>
</template>
<script>
import Loader from '@/components/common/Loader'
import ActionButton from '@/components/common/ActionButton'
import exportersService from '@/services/exporters'
import Alert from '@/components/common/Alert'

export default {
  components: {
    Loader,
    ActionButton,
    Alert
  },
  data() {
    return {
      loader: false,
      pancard: '',
      salesManager: {},
      document_list: [],
      loading: true
    }
  },
  created() {
    this.$validator.extend('length_format', {
      getMessage() {
        return 'PAN should be 10 characters long.'
      },
      validate(value) {
        // Check if PAN length is exactly 10 characters
        return value.length === 10
      }
    })
    this.$validator.extend('pan_format', {
      getMessage() {
        return 'Please enter a valid PAN.'
      },
      validate(value) {
        // Auto convert to uppercase
        if (value) {
          value = value.toUpperCase()
        }
        // PAN regex
        return /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(value)
      }
    })
  },
  async mounted() {
    this.$mpTrack('Identity page mounted')
    this.redirectIfPANPresent()
  },
  methods: {
    async updatePANCardAPI() {
      this.$mpTrack('Pan Details - Update Pan Card clicked')
      if (this.pancard) {
        const res = await exportersService.updatePanCard(
          this.$store.getters.accountId,
          { pan_number: this.pancard.toUpperCase() }
        )

        if (res.data) {
          this.$mpTrack('Pan Details - PAN card updated successfully', {
            data: res.data
          })
          this.$store.dispatch('setPANStatus', {
            panPending: false
          })
          this.$router.push({ name: 'dashboard' })

          return this.$notify({
            title: 'PAN card updated successfully!',
            type: 'success'
          })
        } else {
          this.$mpTrack('Pan Details - PAN card failed to update', {
            data: res.data
          })
          this.$notify({
            title: 'Failed to update!',
            type: 'error'
          })
        }
      } else {
        this.$notify({
          title: 'PAN not added!',
          type: 'error'
        })
      }
    },
    async redirectIfPANPresent() {
      this.loading = true
      const res = await exportersService.checkPanNumber(
        this.$store.getters.accountId
      )

      const isPANNotPresent = !res.data.pan_present
      const notAnIndianExporter = !this.isIndianExporter

      if (isPANNotPresent || notAnIndianExporter) {
        this.$mpTrack('Pan Details - forced redirected to dashboard', {
          data: res.data
        })
        this.$router.push({ name: 'dashboard' })
        return
      }
      this.loading = false
    },
    submitPanCard() {
      return this.$validator.validate().then((result) => {
        if (result) {
          // PAN detail submit API
          this.updatePANCardAPI()
        }
      })
    }
  },
  computed: {
    isIndianExporter() {
      return (
        this.$store.state.account &&
        this.$store.state.account.account &&
        this.$store.state.account.country === 'India' &&
        this.$store.state.account.account.role === 'exporters'
      )
    }
  }
}
</script>
<style lang="scss" scoped>
input {
  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(39, 182, 124, 0.25);
  }
}
</style>
<style lang="scss" scoped>
.legal-container {
  text-align: center;
  .legal-inner-container {
    margin: 48px auto;
    @media screen and (max-width: $tablet) {
      margin: 12px auto;
      width: 100%;
    }
    .documents-section {
      width: 480px;
      margin: 0 auto;
      @media screen and (max-width: $tablet) {
        width: 100%;
        margin: auto;
      }
    }
    .legal-input-container {
      margin: auto;
      width: 480px;
      @media screen and (max-width: $tablet) {
        width: 100%;
      }
    }
    .top-border {
      border-top: 1px solid $wealthy-green-500;
    }
    .legal-information-section {
      text-align: left;
      color: $noble-blue-500;
      border-top: 1px solid $wealthy-green-500;
    }
  }
}
.input-container {
  width: 100%;
  margin-bottom: 16px;
  .input-label {
    font-size: 16px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
  }
  .input-block {
    display: flex;
    flex-direction: column;
    text-align: left;
    .date-picker {
      .action-input {
        position: absolute;
        right: 0;
        height: 100%;
        width: 50px;
        top: 0;
        background: transparent;
        color: $wealthy-green-500;
        display: flex;
        align-items: center;
        justify-content: center;
        border-end-end-radius: 5px;
        border-start-end-radius: 5px;
        cursor: pointer;
      }
    }
    .drip-dropdown {
      height: 56px;
      width: 100%;
      max-width: 400px;
      ::v-deep .multiselect__select {
        height: 100%;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 24px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $neutral-gray-500;
        font-weight: 400;
        margin-bottom: 4px;
        .multiselect__placeholder {
          color: $neutral-gray-500;
        }
        .multiselect__single {
          color: $noble-blue-500;
          font-weight: 700;
          vertical-align: bottom;
        }
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    input {
      outline: 0px;
      width: 100%;
      max-width: 480px;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      background: $white;
      color: $noble-blue-500 !important;
      font-size: 16px;
      font-weight: 700;
      &::placeholder {
        font-weight: 400;
        background: $white;
        color: $neutral-gray-500;
      }
      &.width-800 {
        max-width: 800px;
      }
    }
  }
}
.width-200 {
  text-align: left;
  min-width: 200px;
}
</style>
<style lang="scss">
.message {
  font-size: 16px !important;
  padding-left: 12px !important;
}
.icon {
  img {
    width: 24px;
  }
}
</style>
